import React, { useState, useEffect } from 'react';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import SettingsCard from '../../components/ui/SettingsCard';
import { useApi } from '@/lib/useApi';

interface Contractor {
  id: string;
  company: string;
  verified: boolean;
  user: {
    email: string;
    phone: string;
  };
}

interface AdminEditContractorProfileProps {
  contractorId: string;
  onNavigate: (screen: string) => void;
}

const AdminEditContractorProfile: React.FC<AdminEditContractorProfileProps> = ({
  contractorId,
  onNavigate
}) => {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [contractor, setContractor] = useState<Contractor | null>(null);

  // Contractor information state
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [verified, setVerified] = useState(false);
  // CB:BEGIN bid-fee-override-state
  const [bidFeeOverride, setBidFeeOverride] = useState<'DEFAULT' | 'NO_BID_FEE' | 'BID_FEE'>('DEFAULT');
  // CB:END bid-fee-override-state

  useEffect(() => {
    fetchContractorData();
  }, [contractorId]);

  const fetchContractorData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/admin/contractors/${contractorId}`);
      const data = response.contractor || response; // Handle both response formats
      
      setContractor(data);

      // Set contractor information
      setCompany(data.companyName || data.company || '');
      setEmail(data.user?.email || '');
      setPhone(data.user?.phone || '');
      setVerified(data.verified || false);
      // CB:BEGIN bid-fee-override-load
      setBidFeeOverride(data.bidFeeOverride || 'DEFAULT');
      // CB:END bid-fee-override-load
    } catch (err) {
      console.error('Error fetching contractor data:', err);
      setError('Failed to load contractor data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Update contractor information
      if (contractor?.id) {
        await api.put(`/api/admin/contractors/${contractor.id}`, {
          companyName: company,
          email,
          phone,
          verified
        });
        
        // CB:BEGIN bid-fee-override-save
        // Update bid fee override
        await api.put(`/api/admin/contractors/${contractor.id}/bid-fee-override`, {
          bidFeeOverride
        });
        // CB:END bid-fee-override-save
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        fetchContractorData(); // Refresh data
        // Notify parent component that data was updated
        onNavigate('admin-contractors-list');
      }, 2000);
    } catch (err) {
      console.error('Error saving contractor data:', err);
      setError('Failed to save contractor changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!confirm('Are you sure you want to delete this contractor profile? This action cannot be undone and will delete all associated data.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await api.delete(`/api/admin/contractors/${contractorId}`);

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        // Navigate back to contractors list after successful deletion
        onNavigate('admin-contractors-list');
      }, 2000);
    } catch (err) {
      console.error('Error deleting contractor profile:', err);
      setError(err.message || 'Failed to delete contractor profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: 18, color: '#6b7280' }}>Loading contractor data...</div>
      </div>
    );
  }

  if (!contractor) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: 18, color: '#ef4444' }}>Contractor not found</div>
      </div>
    );
  }

  return (
    <ResponsiveContainer className="container">
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => onNavigate('admin-contractors-list')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'transparent',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            padding: '8px 16px',
            fontSize: '14px',
            color: '#374151',
            cursor: 'pointer',
            marginBottom: '16px'
          }}
        >
          ← Back to Contractors
        </button>
        <h1 style={{ fontSize: 24, fontWeight: '600', color: '#111827', margin: 0 }}>
          Edit Profile - {contractor.company}
        </h1>
        <p style={{ fontSize: 14, color: '#6b7280', margin: '8px 0 0 0' }}>
          Edit contractor personal information and company details
        </p>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: 24,
          color: '#dc2626'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {success && (
        <div style={{
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: 24,
          color: '#16a34a'
        }}>
          <strong>Success:</strong> Changes saved successfully!
        </div>
      )}

      {/* Contractor Information */}
      <SettingsCard title="Contractor Information">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
              Company Name
            </label>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
              Phone
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#fff'
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
              Verification Status
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={verified}
                onChange={(e) => setVerified(e.target.checked)}
                style={{
                  width: '16px',
                  height: '16px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontSize: 14, color: '#374151' }}>
                {verified ? 'Verified Contractor' : 'Unverified Contractor'}
              </span>
            </div>
          </div>
          {/* CB:BEGIN bid-fee-override-toggle */}
          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: '600', color: '#374151', marginBottom: 6 }}>
              Bid Fee Override
            </label>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setBidFeeOverride('DEFAULT')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: bidFeeOverride === 'DEFAULT' ? '#7c3aed' : 'white',
                  color: bidFeeOverride === 'DEFAULT' ? 'white' : '#374151'
                }}
              >
                Default
              </button>
              <button
                type="button"
                onClick={() => setBidFeeOverride('NO_BID_FEE')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: bidFeeOverride === 'NO_BID_FEE' ? '#10b981' : 'white',
                  color: bidFeeOverride === 'NO_BID_FEE' ? 'white' : '#374151'
                }}
              >
                No Bid Fee
              </button>
              <button
                type="button"
                onClick={() => setBidFeeOverride('BID_FEE')}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: bidFeeOverride === 'BID_FEE' ? '#dc2626' : 'white',
                  color: bidFeeOverride === 'BID_FEE' ? 'white' : '#374151'
                }}
              >
                Bid Fee Required
              </button>
            </div>
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              {bidFeeOverride === 'DEFAULT' && 'Contractor pays standard bid fee'}
              {bidFeeOverride === 'NO_BID_FEE' && 'Contractor does not pay bid fee'}
              {bidFeeOverride === 'BID_FEE' && 'Contractor must pay bid fee (enforced)'}
            </p>
          </div>
          {/* CB:END bid-fee-override-toggle */}
        </div>
      </SettingsCard>

      {/* Save and Delete Buttons */}
      <div style={{ marginTop: 24, textAlign: 'center', display: 'flex', gap: 16, justifyContent: 'center' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            backgroundColor: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
            minWidth: '200px'
          }}
        >
          {saving ? 'Saving...' : 'Save Profile Changes'}
        </button>
        <button
          onClick={handleDeleteProfile}
          disabled={saving}
          style={{
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.7 : 1,
            minWidth: '200px'
          }}
        >
          {saving ? 'Deleting...' : 'Delete Profile'}
        </button>
      </div>
    </ResponsiveContainer>
  );
};

export default AdminEditContractorProfile;
